import { useEffect, useRef } from 'react';
import { useOrderSocketContext } from './OrderSocketContext';
import type { OrderStatusEvent, NewOrderEvent } from './OrderSocketContext';

export type { OrderStatusEvent, NewOrderEvent } from './OrderSocketContext';

export type SocketStatus = 'connecting' | 'open' | 'closed';

interface UseOrderSocketOptions {
  onOrderStatus?: (event: OrderStatusEvent) => void;
  onNewOrder?: (event: NewOrderEvent) => void;
  enabled?: boolean;
}

/**
 * Subscribes to the shared order WebSocket via context.
 * Multiple components can call this hook; they all share one connection.
 */
export const useOrderSocket = ({
  onOrderStatus,
  onNewOrder,
  enabled = true,
}: UseOrderSocketOptions = {}) => {
  const { subscribe, status } = useOrderSocketContext();

  const onOrderStatusRef = useRef(onOrderStatus);
  const onNewOrderRef = useRef(onNewOrder);
  useEffect(() => { onOrderStatusRef.current = onOrderStatus; }, [onOrderStatus]);
  useEffect(() => { onNewOrderRef.current = onNewOrder; }, [onNewOrder]);

  useEffect(() => {
    if (!enabled) return;

    const unsub = subscribe({
      onOrderStatus: (event: OrderStatusEvent) => {
        onOrderStatusRef.current?.(event);
      },
      onNewOrder: (event: NewOrderEvent) => {
        onNewOrderRef.current?.(event);
      },
    });

    return unsub;
  }, [enabled, subscribe]);

  return { status, reconnect: () => {} };
};
